import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogContent,
    Button,
    Typography,
    Box,
    IconButton,
    CircularProgress,
} from '@mui/material';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import GroupedMultiSelect from './GroupedMultiSelect.comp';

const ORANGE = 'var(--brand-primary, #FF6900)';
const MUTED = 'var(--brand-text-muted, #6b6b6b)';
const BORDER = 'var(--brand-border, #e3e0dc)';
const AMBER_TEXT = '#B86A00';
const AMBER_BAR = '#E08A00';
const AMBER_BG = 'rgba(241, 168, 36, 0.10)';
const AMBER_BORDER = 'rgba(241, 168, 36, 0.45)';

/**
 * Publish Learning Space — My Classes deck, 30 Sep 2026 (C1-C8), built to the
 * HTML flow "PbDQ_SNW, SW_My Classes_Flow".
 *
 *   LearnerSpaceScopeDialog        whole school / by grade / by class
 *     ├── enough  → ConfirmPublishLearnerSpacesDialog → LearningSpacePublishedDialog
 *     └── short   → InsufficientLicencesDialog (request licences from the partner,
 *                   or change the selection)
 *
 * The dialog adds up the per-class counts the preview returned to draw the
 * licence bar before the click. It never sends a count back: publishing sends
 * only `section_codes`, and the server recounts and refuses on its own numbers,
 * because a learner can be enrolled while this is open.
 */

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : (/(s|sh|ch|x)$/.test(word) ? 'es' : 's')}`;

export const gradeLabel = (grade) => {
    const g = String(grade || '').trim();
    if (!g) return 'No grade';
    return /^\d+$/.test(g) ? `Grade ${g}` : g;
};

/** Kindergarten first, then by the number in the grade, then by class name. */
const gradeRank = (grade) => {
    const g = String(grade || '');
    if (/^k(inder|g)/i.test(g)) return -1;
    const n = g.match(/\d+/);
    return n ? Number(n[0]) : Number.MAX_SAFE_INTEGER;
};
export const sortClassesByGrade = (classes) => [...(classes || [])].sort((a, b) =>
    (gradeRank(a.grade) - gradeRank(b.grade))
    || String(a.grade || '').localeCompare(String(b.grade || ''))
    || String(a.section_name || '').localeCompare(String(b.section_name || ''), undefined, { numeric: true }));

const classLabel = (c) => {
    const name = String(c?.section_name || c?.section_code || '').trim();
    return /^class\b/i.test(name) ? name : `Class ${name}`;
};

const pill = {
    borderRadius: '999px', textTransform: 'none', fontWeight: 700, fontSize: '15px', height: 50, px: 3.5, boxShadow: 'none',
};
const primaryBtn = {
    ...pill, backgroundColor: ORANGE, color: '#fff',
    '&:hover': { backgroundColor: '#c9541e', boxShadow: 'none' },
    '&.Mui-disabled': { backgroundColor: 'rgba(241, 90, 36, 0.35)', color: '#fff' },
};
const secondaryBtn = { ...pill, border: `1px solid ${BORDER}`, color: 'var(--brand-text, #111)' };

const SuccessIcon = () => (
    <Box sx={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: 'rgba(46,125,50,0.12)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <CheckIcon style={{ fontSize: 30, color: '#2e7d32' }} />
    </Box>
);

const WarnIcon = () => (
    <Box sx={{ width: 52, height: 52, borderRadius: '50%', backgroundColor: AMBER_BG, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ color: AMBER_TEXT, fontWeight: 800, fontSize: '22px' }}>!</Typography>
    </Box>
);

const SummaryRows = ({ rows }) => (
    <Box sx={{ mt: 2, textAlign: 'left' }}>
        {rows.map((r, i) => (
            <Box
                key={r.k}
                sx={{
                    display: 'flex', justifyContent: 'space-between', gap: 2, py: 1.4,
                    borderTop: `1px solid ${BORDER}`, borderBottom: i === rows.length - 1 ? `1px solid ${BORDER}` : 'none',
                }}
            >
                <Typography sx={{ fontSize: '14px', color: MUTED }}>{r.k}</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, fontFamily: 'monospace' }}>{r.v}</Typography>
            </Box>
        ))}
    </Box>
);

const shortDate = (at) => {
    const d = at ? new Date(at) : null;
    if (!d || Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * "You asked your partner for 36 licences on 8 Oct 2026" — one line per open
 * request (one per school, inside a network). Null when there are none.
 */
const OpenLicenceRequestsNote = ({ requests = [], named = false, sx = {} }) => {
    if (!requests.length) return null;
    return (
        <Box sx={{ ...sx }}>
            {requests.map((r) => (
                <Typography key={r.request_id} sx={{ fontSize: '12.5px', color: MUTED }}>
                    {named && r.school_name ? `${r.school_name}: ` : ''}
                    You asked your partner for {plural(r.requested_licences, 'licence')}
                    {shortDate(r.requested_at) ? ` on ${shortDate(r.requested_at)}` : ''} — waiting on their review.
                </Typography>
            ))}
        </Box>
    );
};

const publishedStamp = (at) => {
    const d = at ? new Date(at) : null;
    if (!d || Number.isNaN(d.getTime())) return null;
    const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `Published on ${date} · ${time}`;
};

/**
 * What a scope selection adds up to. `section_codes` includes already-live
 * classes on purpose (whole school / a part-published grade): the server
 * skips them, and they are what "N already active (skipped)" reports.
 */
export const summariseSelection = (classes, { mode, grades = [], classCodes = [] }, scopeName = '') => {
    let picked;
    if (mode === 'school') picked = classes;
    else if (mode === 'grade') picked = classes.filter((c) => grades.includes(String(c.grade)));
    else picked = classes.filter((c) => classCodes.includes(String(c.section_code)) && !c.is_published);

    const drafts = picked.filter((c) => !c.is_published);
    const live = picked.filter((c) => c.is_published);
    const sum = (list, k) => list.reduce((n, c) => n + (Number(c[k]) || 0), 0);

    let label;
    if (mode === 'school') label = scopeName;
    else if (mode === 'grade') label = [...grades].map(gradeLabel).join(', ');
    else label = drafts.map(classLabel).join(', ');

    return {
        mode,
        label,
        section_codes: picked.map((c) => String(c.section_code)),
        to_activate: sum(drafts, 'student_count'),
        already_active: sum(live, 'student_count'),
        unreachable: sum(drafts, 'unreachable'),
        consent_not_set: drafts.some((c) => c.consent_not_set),
        school_not_published: drafts.some((c) => c.school_not_published),
    };
};

// ─── C1-C3, C6. Who gets a learning space? ───────────────────────────────────

const RadioCard = ({ on, title, subtitle, count, onClick, children }) => (
    <Box
        sx={{
            border: on ? `1.5px solid ${ORANGE}` : '1px solid #EFE4DA',
            backgroundColor: on ? 'rgba(241,90,36,0.05)' : 'var(--brand-surface, #fff)',
            borderRadius: '16px', mb: 1.5,
        }}
    >
        <Box
            role="radio"
            aria-checked={on}
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onClick(); } }}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75, px: 2.75, py: 2.25, cursor: 'pointer' }}
        >
            <Box sx={{
                width: 20, height: 20, borderRadius: '50%', border: `2px solid ${on ? ORANGE : '#C9C2BB'}`,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none', mt: '1px',
            }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: on ? ORANGE : 'transparent' }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>{title}</Typography>
                <Typography sx={{ fontSize: '13.5px', color: MUTED }}>{subtitle}</Typography>
            </Box>
            <Typography sx={{ fontSize: '15px', fontFamily: 'monospace', color: MUTED }}>{count}</Typography>
        </Box>
        {on && children ? <Box sx={{ px: 2.75, pb: 2.25, pl: 7 }}>{children}</Box> : null}
    </Box>
);

export const LearnerSpaceScopeDialog = ({
    open,
    onClose,
    onContinue,
    onRequestLicences,
    loading = false,
    classes = [],
    grades = [],
    licenses = { total: 0, used: 0, available: 0 },
    scopeName = '',
    schoolNameFor = null,
    // Licence requests already with the partner (publish preview).
    openLicenceRequests = [],
    // Bumped by the page for a fresh start. Coming back from Confirm or
    // "Change selection" keeps what was picked, as the flow does.
    resetToken = 0,
}) => {
    const [mode, setMode] = useState('school');
    const [pickedGrades, setPickedGrades] = useState([]);
    const [pickedClasses, setPickedClasses] = useState([]);

    useEffect(() => {
        setMode('school');
        setPickedGrades([]);
        setPickedClasses([]);
    }, [resetToken]);

    const sel = useMemo(
        () => summariseSelection(classes, { mode, grades: pickedGrades, classCodes: pickedClasses }, scopeName),
        [classes, mode, pickedGrades, pickedClasses, scopeName],
    );
    const allDrafts = useMemo(
        () => classes.filter((c) => !c.is_published).reduce((n, c) => n + (Number(c.student_count) || 0), 0),
        [classes],
    );

    const available = Number(licenses.available) || 0;
    const shortfall = Math.max(0, sel.to_activate - available);
    const short = shortfall > 0;
    // A draft school comes first: it has no consent method to set up yet either.
    const blockedBy = sel.school_not_published ? 'school'
        : (sel.consent_not_set ? 'consent' : (sel.unreachable > 0 ? 'unreachable' : null));
    const barPct = !sel.to_activate ? 0 : (available ? Math.min(sel.to_activate / available, 1) * 100 : 100);

    const gradeOptions = grades.map((g) => ({
        value: String(g.grade),
        label: gradeLabel(g.grade),
        meta: `${plural(g.to_activate, 'learner')} · ${plural(g.class_count, 'class')}`,
        disabled: !!g.fully_published,
        disabledLabel: 'Published',
    }));
    const classOptions = sortClassesByGrade(classes).map((c) => {
        const school = schoolNameFor ? schoolNameFor(c) : '';
        return {
            value: String(c.section_code),
            label: school ? `${classLabel(c)} · ${school}` : classLabel(c),
            group: gradeLabel(c.grade),
            meta: plural(Number(c.student_count) || 0, 'learner'),
            disabled: !!c.is_published,
            disabledLabel: 'Published',
        };
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
            <DialogContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                    <Box>
                        <Typography sx={{ fontSize: '26px', fontWeight: 800 }}>Who gets a learning space?</Typography>
                        <Typography sx={{ fontSize: '14.5px', color: MUTED, mt: 0.75 }}>
                            Choose the learners who should be able to sign in and start their programs.
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
                ) : (
                    <>
                        <RadioCard
                            on={mode === 'school'}
                            title="Whole school"
                            subtitle="Every enrolled learner, all grades."
                            count={allDrafts}
                            onClick={() => setMode('school')}
                        />
                        <RadioCard
                            on={mode === 'grade'}
                            title="By grade"
                            subtitle="Select one or more grades."
                            count={mode === 'grade' ? sel.to_activate : allDrafts}
                            onClick={() => setMode('grade')}
                        >
                            <GroupedMultiSelect
                                options={gradeOptions}
                                value={pickedGrades}
                                onChange={setPickedGrades}
                                placeholder="Select grades"
                                size="medium"
                            />
                        </RadioCard>
                        <RadioCard
                            on={mode === 'class'}
                            title="By class"
                            subtitle="Select one or more classes."
                            count={mode === 'class' ? sel.to_activate : 0}
                            onClick={() => setMode('class')}
                        >
                            <GroupedMultiSelect
                                options={classOptions}
                                value={pickedClasses}
                                onChange={setPickedClasses}
                                placeholder="Select classes"
                                size="medium"
                            />
                        </RadioCard>

                        <Box sx={{ backgroundColor: 'var(--brand-bg, #FFFBF7)', border: '1px solid #F4E6D9', borderRadius: '16px', px: 2.75, py: 2, mt: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                                <Typography sx={{ fontSize: '15px', fontWeight: 700 }}>{plural(sel.to_activate, 'learner')} selected</Typography>
                                <Typography sx={{ fontSize: '13.5px', color: MUTED }}>
                                    {sel.already_active ? `${sel.already_active} already active (skipped)` : 'None already active'}
                                </Typography>
                            </Box>
                            <Box sx={{ height: 8, borderRadius: 999, backgroundColor: 'rgba(0,0,0,0.07)', my: 1.5, overflow: 'hidden' }}>
                                <Box sx={{ height: '100%', borderRadius: 999, width: `${barPct}%`, backgroundColor: short ? AMBER_BAR : ORANGE, transition: 'width .25s' }} />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                                <Typography sx={{ fontSize: '13.5px', color: MUTED }}>{plural(available, 'licence')} available</Typography>
                                <Typography sx={{ fontSize: '13.5px', color: short ? AMBER_TEXT : MUTED, fontWeight: short ? 600 : 400 }}>
                                    {short
                                        ? `${plural(shortfall, 'more learner licence')} needed`
                                        : `${plural(available - sel.to_activate, 'licence')} remaining after publishing`}
                                </Typography>
                            </Box>
                        </Box>

                        {short && (
                            <Box sx={{ border: `1px solid ${AMBER_BORDER}`, backgroundColor: AMBER_BG, borderRadius: '14px', px: 2.5, py: 1.75, mt: 1.5 }}>
                                <Typography sx={{ fontSize: '14.5px', fontWeight: 700, color: AMBER_TEXT }}>
                                    {plural(shortfall, 'more learner licence')} needed
                                </Typography>
                                <Typography sx={{ fontSize: '13px', color: AMBER_TEXT, mt: 0.5 }}>
                                    You have selected {plural(sel.to_activate, 'learner')} and currently have {plural(available, 'licence')} available.
                                    {' '}Request the remaining licences before publishing.
                                </Typography>
                                <OpenLicenceRequestsNote requests={openLicenceRequests} named={openLicenceRequests.length > 1} sx={{ mt: 0.75 }} />
                            </Box>
                        )}

                        {/* The server refuses both of these before spending anything;
                            saying so here beats an error after the click. */}
                        {!short && blockedBy === 'school' && (
                            <Box sx={{ border: `1px solid ${AMBER_BORDER}`, backgroundColor: AMBER_BG, borderRadius: '14px', px: 2.5, py: 1.75, mt: 1.5 }}>
                                <Typography sx={{ fontSize: '13px', color: AMBER_TEXT }}>
                                    A school in this selection hasn't been published yet, so its classes can't be published.
                                    {' '}Publish the school workspace first.
                                </Typography>
                            </Box>
                        )}
                        {!short && blockedBy === 'consent' && (
                            <Box sx={{ border: `1px solid ${AMBER_BORDER}`, backgroundColor: AMBER_BG, borderRadius: '14px', px: 2.5, py: 1.75, mt: 1.5 }}>
                                <Typography sx={{ fontSize: '13px', color: AMBER_TEXT }}>
                                    A school in this selection hasn't confirmed its parental consent method yet, so its classes can't be published.
                                    {' '}Its admin confirms it from their workspace Home.
                                </Typography>
                            </Box>
                        )}
                        {!short && blockedBy === 'unreachable' && (
                            <Box sx={{ border: `1px solid ${AMBER_BORDER}`, backgroundColor: AMBER_BG, borderRadius: '14px', px: 2.5, py: 1.75, mt: 1.5 }}>
                                <Typography sx={{ fontSize: '13px', color: AMBER_TEXT }}>
                                    {plural(sel.unreachable, 'learner')} in this selection {sel.unreachable === 1 ? 'is' : 'are'} missing an email address,
                                    so they can't be sent an activation email. Add the missing emails in Manage Students first.
                                </Typography>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
                            <Button
                                onClick={() => (short ? onRequestLicences(sel, shortfall) : onContinue(sel))}
                                disabled={!sel.to_activate || (!short && !!blockedBy)}
                                sx={{ ...primaryBtn, flex: 1, height: 54 }}
                            >
                                {short ? 'Request more licences' : 'Publish learning spaces'}
                            </Button>
                            <Button onClick={onClose} sx={{ ...secondaryBtn, height: 54 }}>Cancel</Button>
                        </Box>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

// ─── C4. Confirm ─────────────────────────────────────────────────────────────

export const ConfirmPublishLearnerSpacesDialog = ({
    open,
    onClose,
    onConfirm,
    loading = false,
    selection,
    licenses = { available: 0 },
}) => {
    if (!selection) return null;
    const count = selection.to_activate;
    const available = Number(licenses.available) || 0;

    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
            <DialogContent sx={{ p: 4 }}>
                <Typography sx={{ fontSize: '22px', fontWeight: 800 }}>Publish learning spaces?</Typography>
                <Typography sx={{ fontSize: '14.5px', color: MUTED, mt: 1 }}>
                    You are about to create learning spaces for {plural(count, 'learner')} in {selection.label || 'this selection'}.
                </Typography>
                <SummaryRows rows={[
                    { k: 'Learners selected', v: count },
                    { k: 'Licences available', v: available },
                    { k: 'Licences remaining after publishing', v: Math.max(0, available - count) },
                ]} />
                <Typography sx={{ fontSize: '13px', color: MUTED, mt: 2 }}>
                    Once published, learners and parents will be able to access their learning spaces.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
                    <Button
                        onClick={onConfirm}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
                        sx={{ ...primaryBtn, flex: 1 }}
                    >
                        {loading ? 'Publishing…' : 'Confirm'}
                    </Button>
                    <Button onClick={onClose} disabled={loading} sx={{ ...secondaryBtn, flex: 1 }}>Cancel</Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

// ─── C7. Not enough licences ─────────────────────────────────────────────────

export const InsufficientLicencesDialog = ({
    open,
    onClose,
    onRequest,
    onChangeSelection,
    studentsSelected = 0,
    licensesAvailable = 0,
    shortfall = 0,
    requestAvailable = false,
    // The request is on its way to the server.
    requesting = false,
    openLicenceRequests = [],
}) => (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
            <WarnIcon />
            <Typography sx={{ fontSize: '21px', fontWeight: 800, mt: 2 }}>More learner licences are needed</Typography>
            <Typography sx={{ fontSize: '14px', color: MUTED, mt: 1 }}>
                Your partner will review this request. We'll email you once the licences are assigned.
            </Typography>
            <SummaryRows rows={[
                { k: 'Learners selected', v: studentsSelected },
                { k: 'Licences available', v: licensesAvailable },
                { k: 'Additional licences needed', v: shortfall },
            ]} />
            {!requestAvailable && (
                <Typography sx={{ fontSize: '12.5px', color: MUTED, mt: 2 }}>
                    Licence requests aren't available here — ask your partner to assign {plural(shortfall, 'more learner licence')}.
                </Typography>
            )}
            <OpenLicenceRequestsNote requests={openLicenceRequests} named={openLicenceRequests.length > 1} sx={{ mt: 2 }} />
            <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
                <Button
                    onClick={onRequest}
                    disabled={!requestAvailable || requesting}
                    startIcon={requesting ? <CircularProgress size={14} color="inherit" /> : null}
                    sx={{ ...primaryBtn, flex: 1, whiteSpace: 'nowrap', px: 2 }}
                >
                    {requesting ? 'Sending…' : 'Request licences'}
                </Button>
                <Button onClick={onChangeSelection} disabled={requesting} sx={{ ...secondaryBtn, flex: 1, whiteSpace: 'nowrap', px: 2 }}>Change selection</Button>
            </Box>
        </DialogContent>
    </Dialog>
);

// ─── C5. Published ───────────────────────────────────────────────────────────

export const LearningSpacePublishedDialog = ({
    open,
    onClose,
    scopeLabel,
    studentsActivated = 0,
    emailsSent = 0,
    publishedAt,
}) => {
    const stamp = publishedStamp(publishedAt);
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
            <DialogContent sx={{ p: 4, textAlign: 'center' }}>
                <SuccessIcon />
                <Typography sx={{ fontSize: '22px', fontWeight: 800, mt: 2 }}>Learning space published</Typography>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, mt: 1 }}>{scopeLabel || 'Your selection'} is ready to go.</Typography>
                <Typography sx={{ fontSize: '13.5px', color: MUTED, mt: 1.25 }}>
                    Their learning space is now active, and an activation email has been sent to learners and parents.
                </Typography>
                <Typography sx={{ fontSize: '13.5px', color: MUTED, mt: 1 }}>
                    They can now sign in and access the assessments and programs you assigned.
                </Typography>
                <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', mt: 2.5, textAlign: 'left', overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.4 }}>
                        <CheckIcon style={{ fontSize: 17, color: '#2e7d32' }} />
                        <Typography sx={{ fontSize: '13.5px' }}>Learning space is active for {plural(studentsActivated, 'learner')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.4, borderTop: `1px solid ${BORDER}` }}>
                        <CheckIcon style={{ fontSize: 17, color: emailsSent > 0 ? '#2e7d32' : AMBER_TEXT }} />
                        <Typography sx={{ fontSize: '13.5px' }}>
                            {emailsSent > 0
                                ? 'Activation emails sent to learners and parents'
                                : 'No activation emails could be sent — check the learners have contact emails'}
                        </Typography>
                    </Box>
                </Box>
                {stamp && <Typography sx={{ fontSize: '12.5px', color: MUTED, mt: 2, fontFamily: 'monospace' }}>{stamp}</Typography>}
                <Button onClick={onClose} sx={{ ...primaryBtn, mt: 2.5 }}>Back to My Classes</Button>
            </DialogContent>
        </Dialog>
    );
};
